import type { SaunaStateView } from '@jums-dashboard/shared';
import { useQuery, type Query } from '@tanstack/react-query';

import { useAuth } from '@/app/auth';
import { callFunction } from '@/lib/callableClient';

const STALE_TIME_MS = 15_000;
const REFETCH_INTERVAL_MS = 30_000;

export const saunaQueryKey = (homeId: string | undefined) => ['homeAutomation', 'sauna', homeId ?? null] as const;

export type SaunaRefetchIntervalFn = (query: Query<SaunaStateView, Error, SaunaStateView>) => number | false;

export interface UseSaunaOptions {
  /**
   * Refetch cadence in ms. Can be a function for adaptive polling (e.g. tighter
   * cadence while warming/ready). Pass `false` to disable polling.
   */
  refetchInterval?: number | false | SaunaRefetchIntervalFn;
}

export default function useSauna(homeId: string | undefined, options: UseSaunaOptions = {}) {
  const { status } = useAuth();
  const enabled = status === 'authorized' && !!homeId;
  const interval = options.refetchInterval ?? REFETCH_INTERVAL_MS;

  return useQuery<SaunaStateView>({
    queryKey: saunaQueryKey(homeId),
    queryFn: () => callFunction<{ homeId: string }, SaunaStateView>('getSaunaStateCallable', { homeId: homeId! }),
    enabled,
    staleTime: STALE_TIME_MS,
    refetchInterval: enabled ? (interval as number | false) : false,
  });
}
